import React, { useRef, useEffect, useState, useCallback, memo } from "react";
import axios from "axios";
import usePrebidBidding from "./usePrebidBidding";

// Global batching queue
const updateQueue = [];
let batchScheduled = false;

const scheduleBatchUpdate = () => {
  if (batchScheduled) return;
  batchScheduled = true;
  setTimeout(async () => {
    batchScheduled = false; 
    const updatesToSend = updateQueue.splice(0, updateQueue.length);
    if (!updatesToSend.length) return;
    const validUpdates = updatesToSend.filter(update => update.ad_id);
    if (!validUpdates.length) {
      console.warn("[AdUnit] No valid updates to send (ad_id missing)");
      return;
    }
    try {
      await axios.post("/api/updateAdData", { updates: validUpdates });
      //console.log("[AdUnit] Batch updates sent:", validUpdates);
    } catch (error) {
      console.error("[AdUnit] Error sending batch updates:", error);
    }
  }, 2000); 
};

const observerOptions = {
  root: null,
  threshold: 0.01,
  rootMargin: "100px",
};

const observer = new IntersectionObserver(entries => {
  entries.forEach(entry => {
    entry.target._visibilityCallback?.(entry);
  });
}, observerOptions);

const AdUnit = memo(({ adUnitCode, onAdRendered }) => {
  if (!adUnitCode) {
    console.warn("[AdUnit] No adUnitCode provided.");
    return null;
  }

  const adRef = useRef(null);
  const viewStart = useRef(null);
  const adIdRef = useRef(null);
  const hasImpressionSent = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [viewTime, setViewTime] = useState(0);

  const { winningAd, ad_id, cpm, isVideo, bidder } = usePrebidBidding(isVisible ? adUnitCode : null);

  useEffect(() => {
    adIdRef.current = ad_id;
  }, [ad_id]);

  const queueUpdate = useCallback((click = 0, impression = 0, additionalViewTime = 0) => {
    const viewability = additionalViewTime
      ? Math.min(additionalViewTime / 1000, 10).toFixed(2)
      : null;
    if (!adIdRef.current) return;
    updateQueue.push({
      ad_id: adIdRef.current,
      click,
      impression,
      viewability_score: viewability,
    });
    scheduleBatchUpdate();
  }, []);

  const handleVisibilityChange = useCallback(entry => {
    if (entry.isIntersecting) {
      setIsVisible(true);
      viewStart.current = performance.now();
    } else {
      setIsVisible(false);
      if (viewStart.current) {
        const elapsed = performance.now() - viewStart.current;
        setViewTime(prev => prev + elapsed);
        queueUpdate(0, 0, elapsed);
        viewStart.current = null;
      }
    }
  }, [queueUpdate]);

  useEffect(() => {
    const el = adRef.current;
    if (!el) return;
    el._visibilityCallback = handleVisibilityChange;
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      delete el._visibilityCallback;
    };
  }, [handleVisibilityChange]);

  useEffect(() => {
    if (isVisible && ad_id && !hasImpressionSent.current) {
      hasImpressionSent.current = true;
      queueUpdate(0, 1);
    }
  }, [isVisible, ad_id, queueUpdate]);

  const handleAdClick = useCallback(() => {
    if (!adIdRef.current) return;
    queueUpdate(1);
  }, [queueUpdate]);

  const renderAd = useCallback(html => {
    if (!html || !adRef.current) return;
    adRef.current.innerHTML = "";

    if (isVideo) {
        return renderVideoAd(html);
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html.trim();
    return (
        handleImageAd(tempDiv) ||
        handleDirectImageUrl(html) ||
        handleIframeAd(tempDiv)  
    );
  }, [handleAdClick]);

  useEffect(() => {
    if (winningAd && adRef.current) {
      if (adRef.current.getAttribute("data-ad-loaded") === winningAd.adId) {
        console.warn("[AdUnit] Skipping duplicate ad render.");
        return; // 🚀 Stops unnecessary re-renders
      }
      adRef.current.setAttribute("data-ad-loaded", winningAd.adId);
      renderAd(winningAd);
      onAdRendered?.(true);
    }
  }, [winningAd, renderAd, onAdRendered]);


  const handleImageAd = tempDiv => {
    const imgTag = tempDiv.querySelector("img");
    if (!imgTag) return false;
    imgTag.src = imgTag.src.startsWith("//") ? `https:${imgTag.src}` : imgTag.src;
    // Attach both click and contextmenu events for left/right clicks
    imgTag.onclick = handleAdClick;
    imgTag.oncontextmenu = (e) => {
      // Let the context menu show but also register the click
      handleAdClick();
    };
    adRef.current.appendChild(imgTag);
    return true;
  };

  const renderVideoAd = (vastXml) => { 
    if (!adRef.current) return;
    
    const videoElement = document.createElement("video");
    videoElement.setAttribute("controls", "true");
    videoElement.setAttribute("width", "640");
    videoElement.setAttribute("height", "360");

    const sourceElement = document.createElement("source");
    sourceElement.setAttribute("type", "video/mp4");

    // Extract video URL from VAST XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(vastXml, "text/xml");
    const mediaFile = xmlDoc.querySelector("MediaFile");
    if (mediaFile) {
        sourceElement.setAttribute("src", mediaFile.textContent.trim());
    }

    videoElement.appendChild(sourceElement);
    adRef.current.appendChild(videoElement);
};

  const handleDirectImageUrl = (adHtml) => {
    if (typeof adHtml !== "string" || !adHtml.match(/\.(jpeg|jpg|gif|png|webp)$/)) return false;

    const imgElement = document.createElement("img");
    imgElement.src = adHtml.startsWith("//") ? `https:${adHtml}` : adHtml;
    imgElement.alt = "Ad";

    // Set size dynamically when image loads
    imgElement.onload = () => {
      imgElement.width = imgElement.naturalWidth;
      imgElement.height = imgElement.naturalHeight;
      //console.log(`[Ad Render] Image Loaded: ${imgElement.width}x${imgElement.height}`);
    };

    imgElement.onclick = handleAdClick;
    imgElement.oncontextmenu = (e) => {
      handleAdClick();
    };

    adRef.current.appendChild(imgElement);
    return true;
  };

  const handleIframeAd = tempDiv => {
    const iframeTag = tempDiv.querySelector("iframe");
    if (!iframeTag) return false;
    // Attach event listener to the container since iframes might block events
    adRef.current.addEventListener("click", handleAdClick, { once: true });
    adRef.current.appendChild(iframeTag);
    return true;
  };


    
    const commonStyle = "transition-opacity duration-1000 ease-in-out flex justify-center items-center w-full mx-auto pb-4";

    const dynamicAdStyles = ["inarticle-paragraph3", "inarticle-paragraph3-mobile", "inarticle-paragraph14", "inarticle-paragraph14-mobile", "inarticle-paragraph30", "inarticle-paragraph30-mobile"]
      .reduce((acc, key) => ({ ...acc, [key]: commonStyle }), {});

    const adStyles = {
      ...dynamicAdStyles,
      "navbar-ad": "transition-opacity duration-1000 ease-in-out flex pl-4", 
      "sidebar-ad": "transition-opacity duration-1000 ease-in-out flex justify-center items-center w-full mx-auto pt-0 pb-4",
      "footer-ad": "transition-opacity duration-1000 ease-in-out flex justify-center items-center w-full mx-auto pt-4 pb-4",
  };

  const defaultClass = "transition-opacity duration-1000 ease-in-out flex justify-center items-center w-full mx-auto";

  return (
    <div id={adUnitCode} ref={adRef} className={adStyles[adUnitCode] || defaultClass}></div>
  );
});

export default AdUnit;
